import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationService, PortalConfigurationService, RestApiService, UrlService } from '@draeger-academy/common';
import { environment } from '../../../../../src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { CouponCode } from 'models/coupon-code';
@Component({
    selector: 'da-coupon-code',
    templateUrl: './coupon-code.component.html',
    styleUrls: ['./coupon-code.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CouponCodeComponent implements OnInit, OnChanges {
    public couponCodeForm: FormGroup;
    public couponCode: string;
    public showCouponCodeInNote: string;
    public submitted = false;
    public statusCode: string;
    public showUsedCode = false;
    public newAmount: number;
    public couponCodeList: Array<CouponCode> = [];
    public showNewCouponCodeWillBeSentText = false;
    @Input() minusPrice: number;
    @Input() currency: string;
    @Input() totalPrice: number;
    @Output() voucherData: EventEmitter<Array<CouponCode>> = new EventEmitter<Array<CouponCode>>();
    constructor(
                private formBuilder: FormBuilder, 
                private _restApi: RestApiService, 
                public _urlService: UrlService, 
                private _configurationService: ConfigurationService,
                private _portalConfigurationService: PortalConfigurationService
    ) {}

    get f() {
        return this.couponCodeForm.controls;
    }
    public _$statusCode: BehaviorSubject<string> = new BehaviorSubject('');

    public get statusCode$(): Observable<string> {
        return this._$statusCode.asObservable();
    }

    public _$couponCodeList: BehaviorSubject<Array<CouponCode>> = new BehaviorSubject([]);

    public get couponCodeList$(): Observable<Array<CouponCode>> {
        return this._$couponCodeList.asObservable();
    }

    private _$pricePattern: BehaviorSubject<string> = new BehaviorSubject('');
    public get pricePattern$(): Observable<string> {
        return this._$pricePattern.asObservable();
    }

    private _$formattedNewAmount: BehaviorSubject<string> = new BehaviorSubject('');
    public get formattedNewAmount$(): Observable<string> {
        return this._$formattedNewAmount.asObservable();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.minusPrice < 0) {
            this.newAmount = this.minusPrice * -1;
            this.formatingNewAmount();
            this.showNewCouponCodeWillBeSentText = true;
        } else {
            this.showNewCouponCodeWillBeSentText = false;
        }
    }

    ngOnInit() {
        // Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character:
        // tslint:disable-next-line:prettier
        const pattern = new RegExp('^[A-Z0-9 \s-]+$');
        this.couponCodeForm = this.formBuilder.group({
            couponCodeField: [Validators.minLength(18), Validators.pattern(pattern)]
        });
    }

    public useCouponCode() {
        this.submitted = true;
        if (this.couponCodeForm.invalid) {
            return;
        }
        this.requestForCouponCode(this.couponCode);
        this.showCouponCodeInNote = this.couponCode;
        this.couponCode = '';
    }

    public requestForCouponCode(couponCode) {
        if (couponCode.length > 18) {
            this._$statusCode.next('CodeIsTooLong');
        } else if (this.totalPrice === 0) {
            this._$statusCode.next('AlreadyZero');
        } else {
            if (this._$couponCodeList.getValue().length > 0) {
                for (const code of this._$couponCodeList.getValue()) {
                    if (couponCode === code.code || couponCode === code.code.replace(/ /g, '')) {
                        this._$statusCode.next('Expired');
                    } else {
                        this.checkVoucherStatus(couponCode);
                    }
                }
            } else {
                this.checkVoucherStatus(couponCode);
            }
        }
    }

    public checkVoucherStatus(couponCode) {
        const _url = `${environment.APIEndpoint}/api/v1/voucher?vouchercode=${couponCode}`;

        return this._restApi.requestRawBodyPOST<any>(_url, couponCode, null, true, true).then(
            (data) => {
                switch (data.statusCode) {
                    case 'VALID':
                        this._$statusCode.next('Valid');
                        this.showUsedCode = true;
                        this.couponCodeList.push(data);
                        this._$couponCodeList.next(this.couponCodeList);
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    case 'INVALID':
                        this._$statusCode.next('Invalid');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    case 'EXPIRED':
                        this._$statusCode.next('Expired');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    case 'REDEEMED':
                        this._$statusCode.next('Redeemed');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    case 'PERSONALIZED':
                        this._$statusCode.next('Personalized');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    case 'DISTRIBUTED':
                        this._$statusCode.next('Invalid');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    case 'DEFAULT':
                        this._$statusCode.next('Invalid');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                    default:
                        this._$statusCode.next('Invalid');
                        this.voucherData.emit(this._$couponCodeList.getValue());
                        break;
                }
            },
            (error) => {
                console.log(error);
                return false;
            }
        );
    }

    public resetMessage() {
        this.submitted = false;
        if (this._$statusCode.getValue() === 'Valid') {
            this.submitted = true;
        }
    }

    public removeCouponCode(i) {
        this.couponCodeList.splice(i, 1);
        this._$couponCodeList.next(this.couponCodeList);
        this.voucherData.emit(this._$couponCodeList.getValue());
        if (this.couponCodeList.length === 0) {
            this.showUsedCode = false;
        }
    }

    private formatingNewAmount(){      
        if (this._configurationService.isPartnerPage()) {
                this._portalConfigurationService.currencyPattern$.subscribe((currencyPattern) => {
                    this._$formattedNewAmount.next(this.formatPrice(this.newAmount, currencyPattern));
                });
        } else {
            this._$pricePattern.next('#.##0,00 " $"');
            this._$formattedNewAmount.next(this.formatPrice(this.newAmount, this._$pricePattern.getValue()));
        }
    }

    private formatPrice(price: any, formatPattern){
        if (formatPattern){
            return this._configurationService.formatPrice(price, formatPattern, this.currency);
        } else {
            return price;
        }
    }
}
